import React, { useEffect } from 'react'
import { useMediaQuery } from '@react-hook/media-query'
import { useNavigate } from '@tanstack/react-location'
import Carousel from 'shared/components/Carousel'
import AssetItem from 'shared/components/AssetItem'
import Button from 'shared/components/Button'

import { ReactComponent as Arrow } from 'shared/assets/icons/arrowRightSmall.svg'

import { useStore } from 'TourApp/store'
import { priceSum, purchaseType } from 'shared/helpers/general'
import { validateEversetCardLink } from 'shared/helpers/everset'
import { useProperty } from 'TourApp/hooks/useProperty'
import { showPrices } from 'shared/helpers/gallery'

export default function RoomItem({ room, onWebsiteClick, onPreviewEvent, onAssetCardLink, isFirstItem, isLastItem }) {
  const { setWishlistItemId } = useStore()
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const navigate = useNavigate()

  function onPreview() {
    if (!room.photos.length) {
      return
    }
    setWishlistItemId(room.id)
    onPreviewEvent()
    navigate({ to: `/gallery` })
  }

  useEffect(() => {
    setWishlistItemId(null)
  }, [setWishlistItemId])

  const _purchaseType = purchaseType(room.main_vendor.is_rental)

  const printPrice = () => {
    const price = priceSum(room.assets.map((asset) => (asset.listings.length > 0 ? asset.listings[0].price : 0)))

    return (
      price && (
        <>
          <span className="capitalize">{_purchaseType}</span> {room.assets.reduce((acc, x) => acc + (x.count || 1), 0)}
          {' items for '}${price.toLocaleString()}
          {_purchaseType === 'rent' && (
            <span className="lg:text-[18px] opacity-70 ml-[5px] text-[#5F6787] lg:font-normal">mo</span>
          )}
        </>
      )
    )
  }

  const property = useProperty()

  return (
    <div
      className={`lg:border-solid lg:border-t border-[#E5E7ED] lg:pt-[36px] mt-[36px] ${
        isFirstItem && 'lg:mt-[24px]'
      } ${isLastItem && 'mb-[36px]'} lg:flex lg:flex-row lg:mr-[-32px]`}
    >
      <div className="lg:pr-[40px] lg:w-[392px]">
        <div className="hidden lg:flex text-[#5F6787] font-bold text-[14px] leading-[17px] uppercase mt-[16px]">
          {room.main_vendor.full_name}
        </div>
        <h2
          className="text-[22px] leading-[22px] lg:text-[28px] lg:leading-[34px] font-medium flex cursor-pointer hover:underline lg:mt-[5px] lg:font-semibold flex-wrap"
          onClick={onPreview}
          onMouseEnter={() => onPreviewEvent(true)}
        >
          <span className="capitalize mr-[7px]">{room.design_name}</span>
          {room.short_description && <span className="mr-[7px]">{`(${room.short_description})`}</span>}
          <span className="lg:hidden">{`by ${room.main_vendor.full_name}`}</span>
          <Arrow className="ml-[6px] mt-[5px] h-[16px] w-[8px] lg:hidden" />
        </h2>
        <div className="leading-[21px] text-[#5F6787] font-medium mt-[12px] mb-[22px] lg:text-[22px] lg:leading-[22px] lg:mt-[25px] lg:text-[#000] lg:mb-[48px]">
          {showPrices(property) && <span>{printPrice()}</span>}
        </div>
        <div className="hidden lg:flex flex-row">
          <Button
            isLink
            href={room.main_vendor.website_link}
            target="_blank"
            className="!text-black bg-transparent border-solid border-[1px] border-black rounded-full w-[170px] leading-[21px] !text-white"
            onClick={() => {
              validateEversetCardLink(room.assets)
              onWebsiteClick()
            }}
          >
            Brand Website
          </Button>
        </div>
      </div>
      <Carousel
        className="sparx-slider assets-slider !mr-[-16px] lg:!mr-0 lg:w-[calc(100%-392px)]"
        slidesPerView="auto"
        navigation={isDesktop}
        spaceBetween={isDesktop ? 28 : 16}
        items={room.assets.map((asset) => (
          <AssetItem
            key={asset.id}
            asset={asset}
            purchaseType={_purchaseType}
            onClick={() => {
              onAssetCardLink(asset)
              window.open(asset.affiliation_link || asset.web_page, '_blank').focus()
            }}
          />
        ))}
      />
    </div>
  )
}
